import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import CFactsheetDisclaimer from '../../components/CFactsheetDisclaimer';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CModal from '../../components/CModal';
import CPageModule from '../../components/CPageModule';
import LinkTabButton from '../../components/LinkTabButton';
import LinkTabButtonWrapper from '../../components/LinkTabButtonWrapper';
import Page from '../../components/layout/Page';
import PageOutlet from '../../components/layout/PageOutlet';
import { coMultifundFactsheetTabs } from '../../constants/coMultifundFactsheetTabs';
import { EFactsheetTabs } from '../../enums/EFactsheetTabs';
import { PageRoutes } from '../../enums/enums';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import type { IFundTabs } from '../../models/IFundTabs';
import { type IResponse } from '../../models/IResponse';
import { SvMultifundFactsheetService } from '../../services/SvMultifundFactsheetService';
import { addBreadcrumbTextReplacement, purgeBreadcrumbTextReplacementDictForOutlets } from '../../store/appSlice';
import CFactsheetOutletStickyHeader from './_components/CFactsheetOutletStickyHeader';
import CMultifundFactsheetTerms from './_components/CMultifundFactsheetTerms';

interface State {
  tabs: IResponse<IFundTabs>;
  targetScrollProgress?: number;
  termsAccepted?: boolean;
}

export default function PMultifundFactsheetOutlet() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const introRef = useRef<HTMLDivElement>(null);
  const keycloak = useKeycloak();

  const { dashboardOutletDimensions, funds } = useAppSelector((state) => state.dashboard);
  const { simpleFundKey } = useParams();

  const outletLabel = CLocalizedTextStringStatic('globalSectionFactsheet');

  const currentSection = location.pathname.split('/').slice(-1)[0];

  const [state, setState] = useState<State>({
    tabs: {
      state: {
        isLoading: false,
        isError: false
      }
    }
  });

  const fetchTabList = async () => {
    updateStateAttribute<State>(
      {
        tabs: {
          ...state.tabs,
          state: {
            ...state.tabs.state,
            isLoading: true
          }
        }
      },
      setState
    );

    const res = await SvMultifundFactsheetService.getRelevantTabs(simpleFundKey ?? '');

    updateStateAttribute<State>(
      {
        tabs: {
          data: await res.getData(),
          state: {
            isLoading: false,
            isError: !(await res.isSuccess())
          }
        }
      },
      setState
    );
  };

  const checkTermsOfService = async () => {
    const res = await SvMultifundFactsheetService.getTosAgreement(
      simpleFundKey ?? '',
      keycloak.keycloak.authenticated ?? false
    );

    if (await res.isSuccess()) {
      updateStateAttribute<State>(
        {
          termsAccepted: await res.getData()
        },
        setState
      );
    }
  };

  const acceptTermsOfService = async () => {
    const res = await SvMultifundFactsheetService.acceptTosAgreement(
      simpleFundKey ?? '',
      keycloak.keycloak.authenticated ?? false
    );

    if (await res.isSuccess()) {
      updateStateAttribute<State>(
        {
          termsAccepted: true
        },
        setState
      );
    }
  };

  useEffect(() => {
    updateStateAttribute<State>(
      {
        targetScrollProgress: introRef.current?.getBoundingClientRect().bottom
      },
      setState
    );
  }, [dashboardOutletDimensions]);

  useEffect(() => {
    (async () => {
      dispatch(addBreadcrumbTextReplacement([simpleFundKey, funds.find((e) => e.id === simpleFundKey)?.shortName]));

      if (!simpleFundKey) return;

      await Promise.all([fetchTabList(), SvMultifundFactsheetService.countUsage(simpleFundKey), checkTermsOfService()]);
    })();

    return () => {
      dispatch(purgeBreadcrumbTextReplacementDictForOutlets());
    };
  }, [simpleFundKey]);

  return (
    <Page>
      {state.termsAccepted === false && (
        <CModal
          open={true}
          onClose={() => {
            navigate(`/${PageRoutes.NEWS_OUTLET}`);
          }}
        >
          <CMultifundFactsheetTerms onAccept={acceptTermsOfService} />
        </CModal>
      )}

      <div ref={introRef}>
        <CPageModule fullWidth>
          <CPageModule submodule>
            <CLocalizedText dictKey={'fsovFirm'} />
          </CPageModule>

          <LinkTabButtonWrapper id={'factsheet-sections'}>
            {state.tabs.data?.factsheetTabs.map((section: EFactsheetTabs) => (
              <LinkTabButton
                key={section}
                to={{
                  pathname: coMultifundFactsheetTabs[section].path,
                  search: location.search
                }}
                label={section}
                text={<CLocalizedText dictKey={coMultifundFactsheetTabs[section].dictKey} />}
                isSelected={[
                  coMultifundFactsheetTabs[section].path,
                  section === EFactsheetTabs.OVERVIEW ? simpleFundKey : undefined
                ].includes(location.pathname.split('/').pop() ?? '')}
              />
            ))}
          </LinkTabButtonWrapper>
        </CPageModule>
      </div>

      <PageOutlet
        outletKey={PageRoutes.FACTSHEET_OUTLET}
        title={outletLabel}
        stickyHeaderConfig={{
          content: (
            <CFactsheetOutletStickyHeader
              tabs={state.tabs.data?.factsheetTabs ?? []}
              onRequestTabChange={(section) => {
                navigate(coMultifundFactsheetTabs[section].path);
              }}
              currentTab={currentSection === simpleFundKey ? EFactsheetTabs.OVERVIEW : currentSection}
            />
          ),
          targetScrollProgress: state.targetScrollProgress ?? Math.max(),
          pad: true
        }}
      />

      <CFactsheetDisclaimer />
    </Page>
  );
}
